/**
 * Derived unit occupancy from tenancies + rooms (frontend-only).
 * Single source of truth for occupancy labels used in admin UI.
 */

import { normalizeUnitTypeLabel } from "./unitDisplayId";
import { formatDateOnly, todayBusinessDate } from "./datetime";

/**
 * The business day occupancy is measured against, as "YYYY-MM-DD".
 *
 * EUROPE/ZURICH, NOT THE DEVICE. "Is this room occupied today" is a business
 * question, and Vantio's business day is Swiss for every viewer — the backend
 * answers it with `zurich_today()`, `/admin/occupancy` with
 * `todayBusinessDate()`, and this twin now with the same helper. Until it did,
 * it read the device's own calendar fields: correct for somebody sitting in
 * Switzerland, and a day out for the same person in Istanbul or New York, on
 * every instant between Zurich midnight and the device's. A room whose tenant
 * moves in today then read "Reserviert" on one laptop and "Belegt" on another.
 *
 * KEPT AS A SEAM RATHER THAN INLINED. Twenty-seven production call sites name
 * this function, and it says what the date is FOR. Delegating fixes the clock
 * for all of them at once and leaves one place where the rule is stated;
 * replacing it everywhere would scatter the same decision across the codebase.
 *
 * `todayBusinessDate` reads `Date.now()`, so a test pins a boundary instant by
 * pinning the clock rather than by passing a date in.
 */
export function getTodayIsoForOccupancy() {
  return todayBusinessDate();
}

export function parseIsoDate(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw);
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  return s.slice(0, 10);
}

function normalizeTenancyStatus(t) {
  return String(t?.status ?? "").trim().toLowerCase();
}

/**
 * ACTIVE: status active, move_in <= today, move_out null or >= today.
 * Ignores ended / unknown statuses (aligned with backend occupancy/revenue).
 */
export function isTenancyActiveByDates(t, todayIso = getTodayIsoForOccupancy()) {
  if (!t) return false;
  const ds = String(t?.display_status || "").trim().toLowerCase();
  if (ds === "ended") return false;
  const moveIn = parseIsoDate(t?.move_in_date);
  const endIso = parseIsoDate(t?.display_end_date ?? t?.move_out_date);

  if (ds === "notice_given" || ds === "active") {
    if (normalizeTenancyStatus(t) !== "active") return false;
    if (!moveIn || moveIn > todayIso) return false;
    if (endIso && endIso < todayIso) return false;
    return true;
  }
  if (ds === "reserved") return false;

  if (normalizeTenancyStatus(t) !== "active") return false;
  if (!moveIn || moveIn > todayIso) return false;
  const moveOut = t?.move_out_date ? parseIsoDate(t.move_out_date) : null;
  if (moveOut && moveOut < todayIso) return false;
  return true;
}

/**
 * FUTURE (upcoming active tenancy): status active, move_in > today.
 */
export function isTenancyFuture(t, todayIso = getTodayIsoForOccupancy()) {
  if (!t) return false;
  if (normalizeTenancyStatus(t) !== "active") return false;
  const moveIn = parseIsoDate(t?.move_in_date);
  if (!moveIn || moveIn <= todayIso) return false;
  return true;
}

/** RESERVED: status reserved, move_in > today. */
export function isTenancyReservedSlot(t, todayIso = getTodayIsoForOccupancy()) {
  if (!t) return false;
  if (normalizeTenancyStatus(t) !== "reserved") return false;
  const moveIn = parseIsoDate(t?.move_in_date);
  if (!moveIn || moveIn <= todayIso) return false;
  return true;
}

/**
 * Canonical day-level occupancy classification — the JS twin of backend
 * tenancy_state.effective_tenancy_status (Sprint 5A). Single source of truth for
 * room occupancy display: both the status badge AND tenant-name selection call
 * this, so the rule is never duplicated across helpers. A reserved tenancy whose
 * move_in is already in the past is OCCUPIED (the bug this fixes), not "frei".
 * @returns {"occupied" | "reserved" | "inactive"}
 */
export function effectiveTenancyStatus(t, todayIso = getTodayIsoForOccupancy()) {
  if (!t) return "inactive";
  const status = normalizeTenancyStatus(t);
  if (status === "cancelled" || status === "ended") return "inactive";
  const moveIn = parseIsoDate(t?.move_in_date);
  const moveOut = parseIsoDate(t?.move_out_date);
  if (moveOut && moveOut < todayIso) return "inactive"; // moved out
  if (status !== "active" && status !== "reserved") return "inactive";
  if (!moveIn) return "inactive";
  if (moveIn > todayIso) return "reserved"; // upcoming
  if (!moveOut || moveOut >= todayIso) return "occupied";
  return "inactive";
}

/**
 * CRM tenant status from all tenancies for one tenant (caller passes tenant-scoped rows).
 * Aligns with unit/room occupancy: active today → active; future reserved or future active → reserved;
 * no tenancies → inactive; all remaining cancelled → cancelled; otherwise ended
 * (past-only / no current slot).
 *
 * @returns {"active"|"reserved"|"ended"|"cancelled"|"inactive"}
 */
export function deriveTenantOperationalStatus(
  tenantTenancies,
  todayIso = getTodayIsoForOccupancy()
) {
  const list = Array.isArray(tenantTenancies) ? tenantTenancies : [];
  // Sprint 5C: a moved-in reserved tenancy (status reserved + move_in already
  // past, not moved out) is occupying today — it must read "active", not fall
  // through to "ended". Additive: the reserved/inactive/ended branches below
  // stay byte-identical; only this first branch gains the moved-in-reserved case.
  const isMovedInReserved = (t) =>
    normalizeTenancyStatus(t) === "reserved" &&
    effectiveTenancyStatus(t, todayIso) === "occupied";
  if (list.some((t) => isTenancyActiveByDates(t, todayIso) || isMovedInReserved(t))) {
    return "active";
  }
  if (
    list.some(
      (t) => isTenancyReservedSlot(t, todayIso) || isTenancyFuture(t, todayIso)
    )
  ) {
    return "reserved";
  }
  if (list.length === 0) {
    return "inactive";
  }
  // A tenant whose only tenancies were cancelled before move-in has not moved
  // out — they never moved in. Before this branch such a tenant fell through to
  // "ended" and the detail header called them "Ausgezogen", which is the one
  // thing that demonstrably did not happen.
  //
  // EVERY, not SOME, and deliberately last. Cancelled is the weakest signal
  // here: it says a rental relationship was called off, never that one took
  // place. So it may only decide when nothing else is left to decide —
  // "active + cancelled" stays active and "ended + cancelled" stays ended,
  // because the ended tenancy is evidence of real occupancy that a cancelled
  // one cannot overwrite. `some` would invert exactly that. An unrecognised
  // status mixed in also keeps the row out of this branch, so the fallback
  // stays the conservative answer.
  if (list.every((t) => normalizeTenancyStatus(t) === "cancelled")) {
    return "cancelled";
  }
  return "ended";
}

/**
 * Monthly rent from tenancy row (canonical backend field).
 *
 * Phase 4: monthly_rent writes mirror into TenancyRevenue via
 * tenancy_admin_service (Option C bridge). The backend serializer
 * emits monthly_revenue_equivalent for tenancies with rent revenue
 * (see batch_attach_monthly_revenue_equivalent). Missing or null
 * values are treated as 0. No legacy fallback needed.
 *
 * NOTE: If tenancy_admin_service changes the serialization path
 * or batch_attach_monthly_revenue_equivalent stops running on every
 * tenancy response, verify this assumption — silent drift risk.
 */
export function getTenancyMonthlyRentValue(t) {
  if (!t) return 0;
  const equiv = t.monthly_revenue_equivalent ?? t.monthlyRevenueEquivalent;
  if (equiv == null || equiv === "") return 0;
  const n = Number(equiv);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Frontend aggregation helper: sums monthly_revenue_equivalent for active
 * tenancies of a unit. For canonical org-level monthly revenue, use
 * GET /api/admin/revenue/monthly-forecast (added in Phase 5). Kept here for
 * unit-level local aggregation on admin pages that render per-unit rent
 * without a dedicated backend endpoint (AdminApartmentsPage,
 * AdminLandlordDetailPage, AdminPropertyManagerDetailPage,
 * adminUnitCoLivingMetrics).
 */
export function sumActiveTenancyMonthlyRentForUnit(
  unit,
  tenancies,
  todayIso = getTodayIsoForOccupancy()
) {
  if (!unit || !Array.isArray(tenancies)) return 0;
  const uid = String(unit.unitId || unit.id || "").trim();
  let sum = 0;
  for (const t of tenancies) {
    if (String(t.unit_id || t.unitId || "").trim() !== uid) continue;
    // Sprint 5C: count OCCUPIED tenancies (canonical occupancy truth), so the
    // per-unit rent sum matches the occupancy donut's "belegt" — this adds
    // moved-in reserved rooms and keeps future-dated reserved out.
    if (effectiveTenancyStatus(t, todayIso) !== "occupied") continue;
    sum += getTenancyMonthlyRentValue(t);
  }
  return sum;
}

/**
 * Per-room occupancy from tenancies (Co-Living). Matches getUnitOccupancyStatus room loop.
 * @param {object} room
 * @param {object[]|null|undefined} tenancies
 * @returns {null | 'frei' | 'reserviert' | 'belegt'}
 */
export function tenanciesForRoom(room, tenancies) {
  if (!room || !Array.isArray(tenancies)) return [];
  const rid = String(room.room_id || room.roomId || room.id || "");
  return tenancies.filter(
    (t) => String(t.room_id || t.roomId || "") === rid
  );
}

export function getActiveTenancyForRoom(
  room,
  tenancies,
  todayIso = getTodayIsoForOccupancy()
) {
  return (
    tenanciesForRoom(room, tenancies).find(
      (t) => effectiveTenancyStatus(t, todayIso) === "occupied"
    ) || null
  );
}

/**
 * The NEXT tenant of a room: the eligible upcoming tenancy with the EARLIEST
 * `move_in_date`.
 *
 * EARLIEST, NOT FIRST-MATCH. Eligibility is unchanged and still belongs to
 * `effectiveTenancyStatus`; what this function owns is the choice between
 * several eligible ones. The backend answers it in
 * `occupancy_service.compute_room_planning` — "Upcoming reservation. Keep the
 * earliest move_in." — and a first-match selection answered it with whatever
 * the wire happened to send first. `GET /api/admin/tenancies` orders
 * `move_in_date DESC, id`, so that was deterministically the LATEST future
 * tenancy: for a room let to two successive tenants, this page named the second
 * one while the same page's OccupancyMap, reading the backend's
 * `next_tenant_name`, named the first.
 *
 * NO TIE-BREAK, DELIBERATELY. The strict `<` keeps the first row seen when two
 * share a `move_in_date`, exactly as the backend's `mi < nxt[0].move_in_date`
 * does. Two eligible tenancies of one room cannot share one in production — the
 * GiST exclusion constraint forbids the overlap — so ordering them is not a
 * contract either side has, and inventing one here would be a contract the
 * backend does not keep.
 *
 * The caller's array is never sorted: `tenanciesForRoom` hands back its own
 * filtered copy and this is a single pass over it.
 */
export function getFutureTenancyForRoom(
  room,
  tenancies,
  todayIso = getTodayIsoForOccupancy()
) {
  let best = null;
  let bestMoveIn = null;
  for (const t of tenanciesForRoom(room, tenancies)) {
    if (effectiveTenancyStatus(t, todayIso) !== "reserved") continue;
    // An eligible row always has a parseable move-in — `effectiveTenancyStatus`
    // returns "inactive" without one — and ISO dates compare lexicographically,
    // which is how that function already compares them against `todayIso`.
    const moveIn = parseIsoDate(t.move_in_date);
    if (bestMoveIn === null || moveIn < bestMoveIn) {
      best = t;
      bestMoveIn = moveIn;
    }
  }
  return best;
}

export function getRoomOccupancyStatus(room, tenancies) {
  if (!room) return null;
  if (tenancies == null) return null;
  const today = getTodayIsoForOccupancy();
  const roomT = tenanciesForRoom(room, tenancies);
  if (roomT.some((t) => effectiveTenancyStatus(t, today) === "occupied"))
    return "belegt";
  if (roomT.some((t) => effectiveTenancyStatus(t, today) === "reserved"))
    return "reserviert";
  return "frei";
}

/**
 * Is this room part of the CURRENT rentable inventory?
 *
 * `is_active === false` is the only retirement path a room has: `DELETE
 * /api/admin/rooms/{id}` is a hard delete that any tenancy or listing blocks,
 * so a room that was ever let can only be stood down, never removed. The row
 * therefore stays — in the room list, on its card, in the tenancy history — and
 * what changes is that it is no longer capacity.
 *
 * ABSENT MEANS ACTIVE, deliberately: `!== false` rather than `=== true`. A wire
 * that has not sent the field, a locally built draft room and a legacy row all
 * have to read as active, because the alternative silently empties the
 * denominator — the failure mode OC1 spent a slice removing.
 *
 * @param {object} room
 * @returns {boolean}
 */
export function isActiveRoom(room) {
  return !!room && room.is_active !== false;
}

/**
 * Did the canonical occupancy enrichment FAIL for this unit?
 *
 * OC1's fifth wire key. It is not "there is no measurement" — that is a missing
 * entry, and it arrives as `null` counters with this flag `false`. It means the
 * engine raised, and the one thing a consumer must not do then is compute a
 * number of its own.
 *
 * @param {object} unit
 * @returns {boolean}
 */
export function hasOccupancySnapshotError(unit) {
  return unit?.occupancy_snapshot_error === true;
}

/**
 * The canonical Layer-1 room counters carried on a unit row, or null.
 *
 * LAYER 1: active rooms, no lease-window gate. Published by
 * `occupancy_service.get_unit_occupancy_batch` through
 * `set_occupancy_snapshot_fields` on `GET /admin/units`,
 * `/admin/owners/{id}/units`, `/admin/landlords/{id}/units`,
 * `/admin/property-managers/{id}/units` and `/admin/portfolio-map`.
 * `normalizeUnit` spreads the wire object, so the snake_case keys arrive
 * untouched and there is no camelCase alias to prefer.
 *
 * ALL THREE OR NOTHING. `reserved` is as load-bearing as the other two — it is
 * what separates "reserviert" from "frei" — so a row carrying only some of them
 * is not a usable measurement and returns null rather than a half answer.
 *
 * NOT A LEGACY READER. `unit.rooms` (declared, hand-maintained),
 * `unit.occupiedRooms`, `unit.occupancy_status` and `Room.status` are
 * deliberately absent from this function. None of them is current-occupancy
 * truth, and OC1 removed the last code that treated them as such.
 *
 * @param {object} unit
 * @returns {null | {total: number, occupied: number, reserved: number}}
 */
export function canonicalUnitRoomCounts(unit) {
  if (!unit) return null;
  const total = unit.total_rooms_snapshot;
  const occupied = unit.occupied_rooms_snapshot;
  const reserved = unit.reserved_rooms_snapshot;
  if (typeof total !== "number" || !Number.isFinite(total)) return null;
  if (typeof occupied !== "number" || !Number.isFinite(occupied)) return null;
  if (typeof reserved !== "number" || !Number.isFinite(reserved)) return null;
  return { total, occupied, reserved };
}

/**
 * The room rows of one unit that count towards its CURRENT occupancy.
 *
 * Every row belonging to the unit, minus the retired ones. The unit match is
 * the same tolerant one the rest of this module uses, because the wire spells
 * the key `unit_id` and `normalizeUnit` adds `unitId` beside it.
 *
 * @param {object} unit
 * @param {object[]|null|undefined} rooms
 * @returns {object[]}
 */
export function activeRoomsForUnit(unit, rooms) {
  const uid = String(unit?.unitId || unit?.id || "").trim();
  if (!uid) return [];
  return (rooms || []).filter(
    (r) =>
      String(r?.unitId || r?.unit_id || "").trim() === uid && isActiveRoom(r)
  );
}

/**
 * @param {object} unit
 * @param {object[]|null|undefined} rooms
 * @param {object[]|null|undefined} tenancies — null/undefined = data not available
 * @returns {null | 'frei' | 'reserviert' | 'belegt' | 'teilbelegt'}
 */
export function getUnitOccupancyStatus(unit, rooms, tenancies) {
  if (!unit) return null;
  if (tenancies == null) return null;
  // FAIL CLOSED, NEVER FAIL LEGACY (OC1 C5 / OC2b). When the canonical
  // enrichment says it failed, the honest answer is the one this helper already
  // has for "no answer" -- `null`, which `formatOccupancyStatusDe` renders as
  // the dash it has always rendered. Computing a number from `unit.rooms` or
  // from the room rows here would turn a reported failure back into a confident
  // figure, which is exactly the defect OC1 removed.
  if (hasOccupancySnapshotError(unit)) return null;
  const today = getTodayIsoForOccupancy();
  const uid = String(unit.unitId || unit.id || "").trim();
  const unitTenancies = tenancies.filter(
    (t) => String(t.unit_id || t.unitId || "").trim() === uid
  );

  const isCoLiving = normalizeUnitTypeLabel(unit.type) === "Co-Living";

  if (!isCoLiving) {
    let hasActive = false;
    let hasFuture = false;
    for (const t of unitTenancies) {
      const st = effectiveTenancyStatus(t, today);
      if (st === "occupied") hasActive = true;
      else if (st === "reserved") hasFuture = true;
    }
    if (hasActive) return "belegt";
    if (hasFuture) return "reserviert";
    return "frei";
  }

  // THE POPULATION, NOT THE DECISION TABLE (OC2b). The four-way table below is
  // byte-for-byte the one this helper has always applied; what changed is the
  // three numbers fed into it.
  //
  // `unit.rooms` IS NO LONGER A DENOMINATOR. It is a declared, hand-maintained
  // field, and OC2 discovery measured what that cost: a unit declaring 9 rooms
  // with 4 active rooms all occupied read as "teilbelegt" on every admin
  // surface while the backend said 4 of 4. It stays on the wire (OC1 is
  // additive) and stays out of this answer.
  //
  // ORDER OF PREFERENCE:
  //   1. the canonical Layer-1 counters, when the row carries them
  //   2. otherwise the unit's ACTIVE room rows, classified here
  // Both are the same population — active rooms, no lease gate. (2) is not a
  // fallback to legacy truth, it is the same rule computed locally for the rows
  // this caller happens to hold.
  const canonical = canonicalUnitRoomCounts(unit);
  let totalRooms;
  let occupiedRooms;
  let futureRooms;

  if (canonical) {
    totalRooms = canonical.total;
    occupiedRooms = canonical.occupied;
    futureRooms = canonical.reserved;
  } else {
    // ACTIVE ROWS ONLY. A retired room has no tenancy, so classifying it would
    // return "frei" and inflate the denominator with capacity that does not
    // exist — the second half of the D5 defect, independent of the first.
    const unitRooms = activeRoomsForUnit(unit, rooms);
    totalRooms = unitRooms.length;
    occupiedRooms = 0;
    futureRooms = 0;
    for (const room of unitRooms) {
      const occ = getRoomOccupancyStatus(room, unitTenancies);
      if (occ === "belegt") occupiedRooms++;
      else if (occ === "reserviert") futureRooms++;
    }
  }

  // Zero active rooms keeps the branch it has always had. What that SHOWS --
  // "0 %", a dash, or nothing -- is an OC3 decision and is not taken here; OC2b
  // only makes the branch reachable for a unit whose rooms are all retired.
  if (totalRooms <= 0) return null;

  if (occupiedRooms === 0 && futureRooms === 0) return "frei";
  if (occupiedRooms === 0 && futureRooms > 0) return "reserviert";
  if (occupiedRooms >= totalRooms) return "belegt";
  if (occupiedRooms > 0 && occupiedRooms < totalRooms) return "teilbelegt";
  return "frei";
}

export function formatOccupancyStatusDe(key) {
  if (key == null) return "—";
  const m = {
    frei: "Frei",
    reserviert: "Reserviert",
    belegt: "Belegt",
    teilbelegt: "Teilbelegt",
  };
  return m[key] || key;
}

/** Tailwind badge tone for admin Badge / spans (matches AdminUnitDetailPage Badge). */
export function occupancyStatusBadgeTone(key) {
  if (key === "frei") return "slate";
  if (key === "reserviert") return "blue";
  if (key === "teilbelegt") return "orange";
  if (key === "belegt") return "green";
  return "slate";
}

const BADGE_TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-700",
  green: "bg-emerald-100 text-emerald-700",
  orange: "bg-orange-100 text-orange-700",
  blue: "bg-sky-100 text-sky-700",
};

export function occupancyStatusBadgeClassName(statusKey) {
  const tone = occupancyStatusBadgeTone(statusKey);
  return BADGE_TONE_CLASSES[tone] || BADGE_TONE_CLASSES.slate;
}

/**
 * Landlord contract lease start (Vertrag Vermieter).
 * Missing lease_start_date → not started (exclude from revenue / active KPIs).
 */
export function isLandlordContractLeaseStarted(
  unit,
  todayIso = getTodayIsoForOccupancy()
) {
  const d = parseIsoDate(unit?.leaseStartDate ?? unit?.lease_start_date);
  if (d == null) return false;
  return d <= todayIso;
}

function isoDateAddDays(iso, days) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7)) - 1;
  const day = Number(iso.slice(8, 10));
  const dt = new Date(Date.UTC(y, m, day));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/**
 * Single contract state for Vertrag Vermieter (deterministic order).
 * @returns {"active"|"expiring_soon"|"expired"|"ended"|"unknown"}
 */
export function getUnitContractState(unit) {
  if (!unit) return "unknown";
  const ls = String(unit.leaseStatus ?? unit.lease_status ?? "").trim();
  if (ls === "ended") return "ended";

  const start = parseIsoDate(unit?.leaseStartDate ?? unit?.lease_start_date);
  if (start == null) return "unknown";

  const today = getTodayIsoForOccupancy();
  const end = parseIsoDate(unit?.leaseEndDate ?? unit?.lease_end_date);
  if (end != null && end < today) return "expired";

  if (end != null) {
    const limit = isoDateAddDays(today, 60);
    if (limit != null && end <= limit) return "expiring_soon";
  }

  return "active";
}

/**
 * Display label for a room's backend `planning_state` (room-availability sprint).
 *
 * `planning_state` is the BACKEND source of truth (GET /api/admin/occupancy/rooms,
 * computed by occupancy_service.compute_room_planning). This helper ONLY maps it
 * to German display copy — it does NOT recompute the planning state.
 *
 *   free                   → "Jetzt frei"
 *   free_then_reserved     → "Jetzt frei · reserviert ab DD.MM.YYYY für <Name>"
 *   occupied               → "Frei ab DD.MM.YYYY" (or "Belegt" when open-ended)
 *   occupied_then_reserved → "Frei ab DD.MM.YYYY · danach reserviert für <Name>"
 *
 * Date semantics: uses `free_from_date` (the first free day) for "Frei ab"; it
 * never renders a contradictory "belegt bis <same date>". Missing dates/names
 * degrade gracefully. Returns null for an unknown/missing state.
 */
export function roomPlanningLabel(room) {
  const ps = room?.planning_state;
  if (!ps) return null;
  const freeFrom = room?.free_from_date ? formatDateOnly(room.free_from_date) : null;
  const nextName = room?.next_tenant_name || null;
  const nextFrom = room?.next_move_in_date ? formatDateOnly(room.next_move_in_date) : null;

  if (ps === "free") return "Jetzt frei";

  if (ps === "free_then_reserved") {
    let res = "reserviert";
    if (nextFrom && nextName) res = `reserviert ab ${nextFrom} für ${nextName}`;
    else if (nextFrom) res = `reserviert ab ${nextFrom}`;
    else if (nextName) res = `reserviert für ${nextName}`;
    return `Jetzt frei · ${res}`;
  }

  if (ps === "occupied") {
    return freeFrom ? `Frei ab ${freeFrom}` : "Belegt";
  }

  if (ps === "occupied_then_reserved") {
    const base = freeFrom ? `Frei ab ${freeFrom}` : "Belegt";
    const suffix = nextName ? `danach reserviert für ${nextName}` : "danach reserviert";
    return `${base} · ${suffix}`;
  }

  return null;
}

/** Parse a YYYY-MM-DD (date-only) string to a UTC-midnight epoch ms, or null. */
function isoDateOnlyToUtcMs(raw) {
  const d = parseIsoDate(raw);
  if (!d) return null;
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7)) - 1;
  const day = Number(d.slice(8, 10));
  return Date.UTC(y, m, day);
}

/**
 * Visual planning badges for a room, derived from the backend planning fields
 * (room-availability sprint). `planning_state` is the backend source of truth;
 * this helper does NOT recompute it — it only derives display flags.
 *
 *   endingSoon:  planning_state is occupied|occupied_then_reserved AND
 *                free_from_date is between today and today+60 days (inclusive).
 *                NEVER true for free|free_then_reserved (an already-free room
 *                does not "end soon").
 *   hasSuccessor: next_tenant_name present OR is_reserved_future === true.
 *   isFreeNow:    planning_state is free|free_then_reserved.
 *
 * Date math is date-only and computed in UTC (no local-midnight drift). Pass
 * `todayIso` explicitly in tests to keep them deterministic (Regel 18).
 *
 * @returns {{ endingSoon: boolean, hasSuccessor: boolean, isFreeNow: boolean }}
 */
export function roomPlanningBadges(room, todayIso = getTodayIsoForOccupancy()) {
  const ps = room?.planning_state || null;
  const isOccupiedState = ps === "occupied" || ps === "occupied_then_reserved";
  const isFreeNow = ps === "free" || ps === "free_then_reserved";

  let endingSoon = false;
  if (isOccupiedState && room?.free_from_date) {
    const todayMs = isoDateOnlyToUtcMs(todayIso);
    const freeMs = isoDateOnlyToUtcMs(room.free_from_date);
    if (todayMs != null && freeMs != null) {
      const days = Math.round((freeMs - todayMs) / 86400000);
      endingSoon = days >= 0 && days <= 60;
    }
  }

  const hasSuccessor =
    Boolean(room?.next_tenant_name) || room?.is_reserved_future === true;

  return { endingSoon, hasSuccessor, isFreeNow };
}

/** Block new tenancies when landlord lease contract is ended (frontend-only). */
export const UNIT_LANDLORD_LEASE_ENDED_TENANCY_MESSAGE =
  "Diese Einheit ist beendet (Vertrag Vermieter). Es können keine neuen Mietverhältnisse erstellt werden.";
